import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import { toast } from "react-toastify";
import {
  Gear,
  Info,
  Lightning,
  ClipboardText,
  CheckCircle,
} from "@phosphor-icons/react";
import { useAppInfo } from "../../../context/app";
import LoadingScreen from "../../../components/LoadingScreen";

const Configuration = () => {
  const { appId, locationId } = useParams();
  const AppService = useAppServices();
  const [app] = useAppInfo();
  const [appInstall, setAppInstall] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedButton, setCopiedButton] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const installRes = await AppService.app_installs.filter({
        query: `appId=${appId}&locationId=${locationId}`,
      });
      if (installRes?.response) {
        setAppInstall(installRes.response.data);
        setFormData(installRes.response.data?.credentials || {});
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      _id: appInstall?._id,
      credentials: formData,
    };
    const { response } = await AppService.app_installs.update({ payload });
    if (response) toast.success("Configuration saved!");
  };
  const copyToClipboard = (button) => {
    const text = button.copy_url
      ?.replace("{{location.Id}}", locationId)
      ?.replace("{{company.Id}}", appInstall?.company_id);

    // Create a temporary textarea element to hold the text
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";

    // Append the textarea to the body
    document.body.appendChild(textarea);

    // Select the text inside the textarea
    textarea.select();

    // Execute the copy command
    document.execCommand("copy");
    setCopiedButton(button.button_text);
    toast.success(`"${button.button_text}" copied!`);
    // Remove the textarea from the DOM
    document.body.removeChild(textarea);
    setTimeout(() => setCopiedButton(null), 2000);
  };

  if (loading) {
    return <LoadingScreen loadingText={"Loading data..."} />;
  }

  const hasQuickActions = app.copy_buttons?.length > 0;

  return (
    <div className="py-5 bg-light min-vh-100">
      <div className="container">
        {/* Header */}
        <div className="card border-0 shadow-sm mb-5 rounded-4">
          <div
            className="card-body d-flex align-items-center"
            style={{ padding: "1rem" }}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{
                width: "55px",
                height: "55px",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                color: "white",
              }}
            >
              <Gear size={24} weight="bold" />
            </div>
            <div>
              <h4 className="fw-bold mb-1">{app.appName} Configuration</h4>
              <p className="mb-0 text-muted small">
                Manage your app credentials and shortcuts.
              </p>
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div
          className={`row g-4 ${
            !hasQuickActions ? "justify-content-center" : ""
          }`}
        >
          {/* Credentials */}
          <div className={hasQuickActions ? "col-lg-8" : "col-lg-6"}>
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body" style={{ padding: "2rem" }}>
                <h6 className="fw-semibold mb-4 text-primary d-flex align-items-center gap-2">
                  <Gear size={18} className="me-2" />
                  Credentials
                </h6>

                <form onSubmit={handleSubmit}>
                  {app.formFields?.map((field, i) => (
                    <div className="mb-3" key={i}>
                      <label
                        htmlFor={field.key}
                        className="form-label fw-medium small text-dark d-flex align-items-center"
                      >
                        {field.label}
                        {field.short_description && (
                          <span title={field.short_description}>
                            <Info
                              size={14}
                              weight="bold"
                              className="ms-1 text-secondary"
                              style={{ cursor: "help" }}
                            />
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        id={field.key}
                        name={field.key}
                        value={formData[field.key] || ""}
                        onChange={handleChange}
                        placeholder={`Enter ${field.label}`}
                        className="form-control rounded-3 shadow-sm"
                        style={{ height: "3.2rem" }}
                      />
                    </div>
                  ))}

                  <button
                    type="submit"
                    className="btn btn-primary w-100 mt-3 fw-semibold rounded-3"
                    style={{ padding: "0.9rem" }}
                  >
                    Save Configuration
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {hasQuickActions && (
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body" style={{ padding: "1.4rem" }}>
                  <h6 className="fw-semibold mb-3 d-flex align-items-center text-warning">
                    <Lightning size={18} className="me-2" />
                    Quick Actions
                  </h6>

                  <div className="d-grid gap-2 mb-4">
                    {app.copy_buttons.map((button, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => copyToClipboard(button)}
                        className={`btn d-flex align-items-center justify-content-start gap-2 rounded-3 fw-medium ${
                          copiedButton === button.button_text
                            ? "btn-success text-white shadow-sm"
                            : "btn-outline-secondary"
                        }`}
                      >
                        {copiedButton === button.button_text ? (
                          <CheckCircle size={16} weight="bold" />
                        ) : (
                          <ClipboardText size={16} weight="bold" />
                        )}
                        {copiedButton === button.button_text
                          ? "Copied!"
                          : button.button_text}
                      </button>
                    ))}
                  </div>

                  <div className="small p-3 mt-5 border rounded-3 bg-light shadow-sm text-muted d-flex align-items-center gap-2">
                    <Info size={14} className="me-1 text-info" />
                    Click a button to copy the generated URL.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Configuration;
